import React, { useRef } from 'react';
import { useInView } from 'framer-motion';
import { motion } from '../../lib/motionShim';
import { Quote } from 'lucide-react';

const MentorSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  return (
    <section
      id="mentor"
      ref={sectionRef}
      className="relative py-24 md:py-32 bg-ston-bg overflow-hidden"
      data-testid="mentor-section"
    >
      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] rounded-full" style={{ background: 'radial-gradient(closest-side, rgba(147,51,234,0.07), rgba(147,51,234,0.035) 55%, transparent 78%)' }} />
      </div>

      <div className="relative max-w-[900px] mx-auto px-6 md:px-12">
        {/* Section label */}
        <motion.span
          className="section-label inline-block"
          initial={{ opacity: 0, x: -20 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          MENTORED BY
        </motion.span>

        {/* Pull quote headline */}
        <motion.h2
          className="mt-4 font-outfit font-bold leading-tight"
          style={{
            fontSize: 'clamp(30px, 4vw, 46px)',
            letterSpacing: '-1.5px',
          }}
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.15 }}
        >
          <span className="text-ston-text">"The goal isn't just more years. </span>
          <span className="gradient-text">It's better years."</span>
        </motion.h2>

        {/* Quote body */}
        <motion.div
          className="mt-8 space-y-5 text-[17px] text-ston-text-secondary leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <p>
            "Healthcare has become very good at treating illness. But the future is not
            only about adding years to life — it's about helping people stay healthier
            through those years. And better decisions require better context.
          </p>
          <p>
            What I see in STON.FIT is an effort to connect everyday health signals —
            nutrition, movement, sleep, recovery and more — helping individuals
            understand their health while giving nutritionists, dietitians and health
            professionals a more connected view of the people they guide.
          </p>
          <p className="text-ston-purple-light">
            That shift from tracking health to understanding, from fragmented data to
            shared understanding is an important step toward prevention, wellness and
            longevity."
          </p>
          <p className="text-ston-text font-medium pt-1">
            Here's what that shift looks like. ↓
          </p>
        </motion.div>

        {/* Attribution */}
        <motion.div
          className="mt-10 flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-5"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.45 }}
        >
          {/* Photo with purple ring */}
          <div className="relative w-16 h-16 md:w-20 md:h-20 shrink-0">
            <div className="absolute -inset-0.5 rounded-full bg-gradient-to-br from-ston-purple to-ston-purple-light" />
            {/* Source image is a circle inset in a square transparent canvas — scale up to fill */}
            <div className="relative w-16 h-16 md:w-20 md:h-20 rounded-full overflow-hidden border-2 border-ston-bg bg-ston-card">
              <img
                src="/mentor/ashvini-danigond.webp"
                alt="Ashvini Danigond"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover scale-[1.45]"
              />
            </div>
          </div>

          <div>
            <p className="text-base md:text-lg font-outfit font-semibold text-ston-text">
              Ashvini Danigond
            </p>
            <p className="mt-0.5 text-sm text-ston-text-muted">
              Founder &amp; CEO, Manorama Infosolutions
            </p>
            <span className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-ston-purple/40 px-3 py-0.5 text-[11px] font-medium tracking-widest text-ston-purple-light uppercase">
              Mentor · STON.FIT
            </span>
          </div>

          {/* Divider + company logo — framed so Manorama reads as a real institution */}
          <div className="hidden sm:block h-14 w-px bg-ston-border mx-3" />
          <div className="self-start sm:self-center rounded-2xl border border-white/[0.1] bg-white/[0.04] px-6 py-5 flex items-center justify-center">
            <img
              src="/mentor/manorama-logo-trim.png"
              alt="Manorama Infosolutions — Healthcare Solutions"
              loading="lazy"
              decoding="async"
              // trimmed PNG is 367×89 (no internal whitespace) — set width so flexbox can't collapse it
              style={{ height: 48, width: 48 * (367 / 89), maxWidth: 'none' }}
            />
          </div>
        </motion.div>

        {/* Decorative quote mark */}
        <Quote
          size={120}
          className="absolute -top-4 right-6 md:right-12 text-ston-purple/10 rotate-180 pointer-events-none hidden md:block"
          fill="currentColor"
          strokeWidth={0}
        />
      </div>
    </section>
  );
};

export default MentorSection;
